import { orderChatDeliveries } from "./chatDelivery";
import {normalizeCompetitorScanName} from './competitorScanIntent';
export interface SuggestionSelection {value:string;label:string;productId?:number;acceptedMessageId?:string}
/** Match an explicit choice against the original saved response, never the latest picker. */
export function resolveSuggestionChoice(source:any,value:unknown,acceptedMessageId?:string):SuggestionSelection {
 const pill=source?.suggestionPills?.pills?.find((item:any)=>item.value===value);
 if(source?.role!=='assistant'||typeof value!=='string'||!pill)throw new Error('This choice is unavailable. Reopen the original task and try again.');
 if(source.suggestionSelection&&source.suggestionSelection.value!==value)throw new Error('This question has already been answered.');
 return source.suggestionSelection??{value: pill.value,label:pill.label,productId:pill.productId,acceptedMessageId};
}
/** Legacy saved chats did not stamp consumed choices. Require an exact answer plus a saved result. */
export function savedSuggestionSelection(source:any,messages:any[]):SuggestionSelection|undefined {
 if(source.suggestionSelection)return source.suggestionSelection;
 const index=messages.findIndex(message=>message.id===source.id);
 if(index<0)return;
 const later=messages.slice(index+1);
 const answerIndex=later.findIndex(message=>message.role==='user');
 if(answerIndex<0)return;
 const answer=later[answerIndex];
 const pill=source.suggestionPills?.pills?.find((item:any)=>item.value===answer.content);
 if(!pill)return;
 const response=later.slice(answerIndex+1).find(message=>message.role==='assistant');
 if(!response||!(response.angleSetCard||response.conceptsRun?.setId||response.playbookProgress?.isDone||response.playbookJobs?.some((job:any)=>job.status==='completed')))return;
 return {value:pill.value,label:pill.label,productId:pill.productId,acceptedMessageId:response.id};
}

/** Preserve server-owned settings and choice claims across stale client transcript saves. */
export function preserveTaskUIState(prior:any[],incoming:any[]):any[]{
 const byId=new Map(prior.filter(entry=>typeof entry?.id==='string').map(entry=>[entry.id,entry]));
 const merged=incoming.filter(entry=>entry?.role!=='metadata'&&entry?.id!=='server-chat-admission').map(entry=>{
  const saved=byId.get(entry.id);
  const {conceptSteering:_untrustedSteering,suggestionSelection:_untrustedSelection,uiAction:_untrustedUIAction,serverCompetitorChoice:_untrustedCompetitorChoice,serverCompetitorScanChoice:_untrustedScanChoice,serverMyAdsChoice:_untrustedMyAdsChoice,serverCreateAdRequest:_untrustedCreateAdRequest,serverEvidenceRecovery:_untrustedEvidenceRecovery,deliveredAt:_untrustedDelivery,deliveredAfterIds:_untrustedDeliveryAnchors,...next}=entry;
  delete next.serverAdmittedUserMessageId;
  delete next.serverAdmittedUserMessageIds;
  delete next.serverCopyConceptCollection;
  delete next.serverCopyBriefRequest;
  if(saved?.serverCopyConceptCollection)next.serverCopyConceptCollection=saved.serverCopyConceptCollection;
  if(saved?.serverCopyBriefRequest)next.serverCopyBriefRequest=saved.serverCopyBriefRequest;
  if(saved?.conceptDraftProgress?.runId)next.conceptDraftProgress=saved.conceptDraftProgress;
  delete next.identityMutationReceipts;
  if(saved?.identityMutationReceipts)next.identityMutationReceipts=saved.identityMutationReceipts;
  delete next.sourceAssetReferences;
  if(saved?.sourceAssetReferences){next.sourceAssetReferences=saved.sourceAssetReferences;next.thumbnails=saved.thumbnails;}
  delete next.sourceAdReferences;
  if(saved?.sourceAdReferences)next.sourceAdReferences=saved.sourceAdReferences;
  if(saved?.sourceAdReferences?.some((reference:any)=>reference.source==='saved_public_brand_ad_analysis'))next.thumbnails=saved.thumbnails;
  if(saved?.serverCreateAdRequest){next.serverCreateAdRequest=saved.serverCreateAdRequest;next.suggestionPills=saved.suggestionPills;}
  if(saved?.serverEvidenceRecovery){next.serverEvidenceRecovery=saved.serverEvidenceRecovery;next.suggestionPills=saved.suggestionPills;}
  if(saved?.serverMyAdsChoice){next.serverMyAdsChoice=true;next.suggestionPills=saved.suggestionPills;}
  if(saved?.serverCompetitorChoice){next.serverCompetitorChoice=true;next.suggestionPills=saved.suggestionPills;}
  if(saved?.serverCompetitorScanChoice){next.serverCompetitorScanChoice=saved.serverCompetitorScanChoice;next.suggestionPills=saved.suggestionPills;}
  if(saved && Object.hasOwn(saved,'suggestionSelection'))next.suggestionSelection=saved.suggestionSelection;
  if(saved?.uiAction === 'build_campaign' && saved.role === 'user')next.uiAction=saved.uiAction;
  if(saved?.conceptSteering)next.conceptSteering=saved.conceptSteering;
  if(saved?.conceptsRun)next.conceptsRun=saved.conceptsRun;
  else if(next.conceptsRun)delete next.conceptsRun;
  if(saved?.creativeMode)next.creativeMode=saved.creativeMode;
  if(typeof saved?.deliveredAt === "number") return {...next,content:saved.content,deliveredAt:saved.deliveredAt,deliveredAfterIds:saved.deliveredAfterIds,isStreaming:false};
  return next;
 });
 for(const saved of prior){
  if(saved?.id==='server-chat-admission'){merged.push(saved);continue;}
  if(saved?.id==='task-creative-mode'||((typeof saved?.deliveredAt === "number"||saved?.sourceAssetReferences||saved?.serverCopyConceptCollection||saved?.serverCopyBriefRequest||saved?.identityMutationReceipts||saved?.conceptSteering||saved?.conceptsRun||saved?.serverCompetitorChoice||saved?.serverCompetitorScanChoice||saved?.serverMyAdsChoice||saved?.serverCreateAdRequest||saved?.serverEvidenceRecovery||Object.hasOwn(saved??{},'suggestionSelection')||saved?.uiAction === 'build_campaign')&&!merged.some(entry=>entry.id===saved.id)))merged.push(saved);
 }
 return orderChatDeliveries(merged);
}

/** Typed advertiser replies belong to the newest saved competitor question. */
export function typedCompetitorChoice(history:any[],answer:string):{messageId:string;value:string}|undefined {
 const normalized=answer.trim().toLowerCase().replace(/[’']/g,'').replace(/[.!]$/,'');
 for(let index=history.length-1;index>=0;index--){
  const entry=history[index];
  if(entry?.role==='assistant'&&entry.serverCompetitorScanChoice)return;
  const pills=entry?.suggestionPills?.pills;
  if(entry?.role!=='assistant'||!entry.serverCompetitorChoice||!Array.isArray(pills)||!pills.some((pill:any)=>pill.forcedTool?.name==='run_competitor_ads'))continue;
  const matches=pills.filter((pill:any)=>{
   const label=String(pill.label).toLowerCase().replace(/[’']/g,'');
   return normalized===label||normalized===`${label}s`;
  });
  return matches.length===1?{messageId:entry.id,value:matches[0].value}:undefined;
 }
}

/** A bare name can answer only the newest server-issued, unanswered scan question. */
export function typedCompetitorScanChoice(history:any[],answer:string,currentUserMessageId?:string):{messageId:string;value:string}|undefined {
 const normalized=normalizeCompetitorScanName(answer).replace(/[.!]$/,'');
 for(let index=history.length-1;index>=0;index--){
  const entry=history[index];
  if(entry?.role==='user'&&entry.id!==currentUserMessageId)return;
  if(entry?.role!=='assistant'||!entry.suggestionPills)continue;
  if(!entry.serverCompetitorScanChoice||entry.serverCompetitorScanChoice.closed||entry.suggestionSelection)return;
  const pills=entry.suggestionPills.pills;
  if(!Array.isArray(pills))return;
  const matches=pills.filter((pill:any)=>normalized===normalizeCompetitorScanName(String(pill.label)));
  return matches.length===1?{messageId:entry.id,value:matches[0].value}:undefined;
 }
}
